import styled, { x } from '@xstyled/styled-components';
import React, { useEffect, useRef } from 'react';
import { useHistory } from 'react-router';
import { msgid, ngettext, t } from 'ttag';
import { PretestSkipRule } from '~/data';
import { select } from '~/lib/select';
import { resultTitle } from '~/ui/typography/resultTitle';
import { TrainingState, useTrainingStore } from '../state';
import {
  pretestChapterRows,
  pretestOutcomeKind
} from '../state/pretest/pretestOutcome';
import { useTrainingUrl } from '../useTrainingUrl';
import { PretestChapterList } from './PretestChapterList';
import { PretestScoreMeter } from './PretestScoreMeter';
import { PretestButton, PretestScreen } from './PretestScreen';

// Titled like the other course result screens (PassFail, Success), in sentence case: these titles name the course
// or a count of chapters, which capitalizing every word would misspell. The card spaces its own content.
const Title = styled.h2`
  ${resultTitle}
  text-transform: none;
  margin: 0;
  overflow-wrap: break-word;

  &:focus {
    outline: none;
  }
`;

const selector = (s: TrainingState) =>
  select(
    s,
    'pretest',
    'items',
    'status',
    'title',
    'certificateUrl',
    'featureShowCertificate',
    'pretestExitRoute'
  );

/**
 * What the graded pretest gave the learner, as the server decided it. A completed course goes on through the
 * normal completion (policies, review, then the Success screen with the certificate, LMS and postMessage),
 * so nothing here signals completion itself. Under the strict rule there is no passing score: the screen
 * shows the score alone and explains that a chapter is skipped only when every answer about it is right.
 */
export const PretestResults = () => {
  const {
    pretest,
    items,
    status,
    title,
    certificateUrl,
    featureShowCertificate,
    pretestExitRoute
  } = useTrainingStore(selector);
  const url = useTrainingUrl();
  const history = useHistory();
  const titleRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    // The results take the place of the screen the learner submitted from:
    // screen readers start at the outcome, as with a new slide.
    titleRef.current?.focus();
  }, []);

  if (!pretest) return null;

  const kind = pretestOutcomeKind(pretest, status);
  const rows = pretestChapterRows(items, pretest);
  const testedOut = rows.filter(row => row.testedOut).length;
  const remaining = rows.filter(row => !row.testedOut);
  const strict = pretest.skipRule === PretestSkipRule.Strict;
  const passing = strict ? null : pretest.passingScorePercent;
  const go = () => history.push(`${url}/${pretestExitRoute()}`);

  let heading = t`You’ll take the full course`;
  let body = strict
    ? t`To skip a chapter, every answer about it has to be right. The lessons cover everything in this assessment.`
    : t`You needed ${passing}% to pass this assessment. To skip a chapter, every answer about it has to be right. The lessons cover everything in this assessment.`;
  let action = t`Start the course`;
  let certificate: string | null = null;

  if (kind === 'completed') {
    heading = pretest.completedCourse
      ? t`You tested out of ${title}`
      : t`You completed ${title}`;
    body = pretest.completedCourse
      ? t`You’ve completed this course without taking the lessons. You can still review any chapter from the course menu.`
      : t`Your assessment results are saved. You can review any chapter from the course menu.`;
    action = t`Continue`;
    if (featureShowCertificate && certificateUrl) {
      certificate = t`Your certificate is ready on the next screen.`;
      action = t`Continue to your certificate`;
    } else if (featureShowCertificate) {
      certificate = t`Your certificate is being prepared. You’ll also find it in your training portal.`;
    }
  } else if (kind === 'passed') {
    const count = remaining.length;
    heading = t`You passed the assessment`;
    body = ngettext(
      msgid`${count} chapter is still part of your course.`,
      `${count} chapters are still part of your course.`,
      count
    );
    if (featureShowCertificate) {
      certificate = t`You’ll earn your certificate when you finish.`;
    }
  } else if (kind === 'chapters') {
    heading = ngettext(
      msgid`You tested out of ${testedOut} chapter`,
      `You tested out of ${testedOut} chapters`,
      testedOut
    );
    body = strict
      ? t`You’ll skip the chapters where every answer was right and take the rest.`
      : t`You needed ${passing}% to pass this assessment. You’ll skip the chapters where every answer was right and take the rest.`;
  }

  if (kind === 'passed' || kind === 'chapters') {
    const next = remaining[0];
    action = next ? t`Continue to ${next.title}` : t`Continue`;
  }

  return (
    <PretestScreen wide>
      <Title ref={titleRef} tabIndex={-1}>
        {heading}
      </Title>
      {pretest.scorePercent != null && (
        <PretestScoreMeter
          scorePercent={pretest.scorePercent}
          passingScorePercent={passing}
          correctCount={pretest.correctCount}
          questionCount={pretest.questionCount}
        />
      )}
      {(strict ||
        passing != null ||
        kind === 'completed' ||
        kind === 'passed') && <x.p m={0}>{body}</x.p>}
      {certificate && <x.p m={0}>{certificate}</x.p>}
      {kind !== 'completed' && kind !== 'none' && (
        <PretestChapterList rows={rows} />
      )}
      <PretestButton
        w="100%"
        onClick={go}
        data-test-id="pretest-continue-button"
      >
        {action}
      </PretestButton>
    </PretestScreen>
  );
};
